// src/hooks/useLabels.ts

import { useState, useEffect, useCallback } from 'react';
import { LabelRecord, LabelCreate } from '../types/label.types';
import { createLabel, getLabelHistory, getCurrentLabel } from '../services/labelService';
import { useAuth } from '../contexts/AuthContext';

export const useLabels = (audioId?: string) => {
  const { userProfile } = useAuth();
  const [currentLabel, setCurrentLabel] = useState<LabelRecord | null>(null);
  const [labelHistory, setLabelHistory] = useState<LabelRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Carrega o histórico de rótulos de um áudio
   */
  const loadLabelHistory = useCallback(async (targetAudioId: string) => {
    setLoading(true);
    setError(null);

    try {
      const history = await getLabelHistory(targetAudioId);
      setLabelHistory(history.labels);
      setCurrentLabel(history.currentLabel || null);
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar histórico de rótulos');
      setLabelHistory([]);
      setCurrentLabel(null);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Carrega apenas o rótulo atual
   */
  const loadCurrentLabel = useCallback(async (targetAudioId: string) => {
    setLoading(true);
    setError(null);

    try {
      const label = await getCurrentLabel(targetAudioId);
      setCurrentLabel(label);
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar rótulo atual');
      setCurrentLabel(null);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Salva um novo rótulo (criação ou edição)
   */
  const saveLabel = useCallback(async (labelData: LabelCreate): Promise<boolean> => {
    if (!userProfile) {
      setError('Usuário não autenticado');
      return false;
    }

    setLoading(true);
    setError(null);

    try {
      const newLabel = await createLabel(
        labelData,
        userProfile.uid,
        userProfile.displayName || userProfile.email || 'Usuário',
        currentLabel?.labelId // Se existe rótulo atual, passa o ID para versionamento
      );

      // Atualiza o estado local
      setCurrentLabel(newLabel);
      setLabelHistory(prev => [newLabel, ...prev]);

      return true;
    } catch (err: any) {
      setError(err.message || 'Erro ao salvar rótulo');
      return false;
    } finally {
      setLoading(false);
    }
  }, [userProfile, currentLabel]);

  /**
   * Limpa o estado
   */
  const clearLabels = useCallback(() => {
    setCurrentLabel(null);
    setLabelHistory([]);
    setError(null);
  }, []);

  /**
   * Carrega automaticamente quando audioId muda
   */
  useEffect(() => {
    if (audioId) {
      loadLabelHistory(audioId);
    } else {
      clearLabels();
    }
  }, [audioId, loadLabelHistory, clearLabels]);

  return {
    currentLabel,
    labelHistory,
    loading,
    error,
    saveLabel,
    loadLabelHistory,
    loadCurrentLabel,
    clearLabels
  };
};
